import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import { rolesApi, permissoesApi, PermissaoDto, RoleDto } from '../services/api';
import { Plus, Edit, Trash2, Shield, Save } from 'lucide-react';

const RECURSOS = ['mapa','reservas','clientes','mesas','locais','historico','configuracoes','utilizadores','canais','admin_db','perfil','roles','auditoria','estatisticas'] as const;

const PERMISSION_HELP = {
  podeAceder: 'Permite consultar e abrir este recurso.',
  podeCriar: 'Permite criar novos registos neste recurso.',
  podeEditar: 'Permite alterar registos existentes neste recurso.',
  podeEliminar: 'Permite eliminar registos deste recurso.',
  podeGerir: 'Permite administrar o recurso e as suas definições.',
} as const;

export function RolesPage() {
  const { success, error } = useToast();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<RoleDto | null>(null);
  const [perms, setPerms] = useState<PermissaoDto[]>([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDto | null>(null);
  const [formNome, setFormNome] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formAtivo, setFormAtivo] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await rolesApi.getAll();
      if (r.success && r.data) setRoles(r.data);
      if (r.data?.length && !selectedRole) setSelectedRole(r.data[0]);
    } catch (err) {
      console.error('Failed to load roles:', err);
      error('Erro ao carregar roles');
    }
    finally { setLoading(false); }
  }, [error]);

  const loadPerms = useCallback(async (roleId: number) => {
    setPermsLoading(true);
    try {
      const r = await permissoesApi.getAll(roleId);
      if (r.success && r.data) {
        // normaliza para garantir todos recursos existem
        const map = new Map(r.data.map(p => [p.recurso, p]));
        const full: PermissaoDto[] = RECURSOS.map(rec => map.get(rec) ?? { id: 0, role: roleId, recurso: rec, podeAceder: false, podeCriar: false, podeEditar: false, podeEliminar: false, podeGerir: false, versao: 1 });
        setPerms(full);
      }
    } catch (err) {
      console.error('Failed to load permissões:', err);
      error('Erro ao carregar permissões');
    }
    finally { setPermsLoading(false); }
  }, [error]);

  useEffect(()=>{ loadRoles(); }, [loadRoles]);
  useEffect(()=>{ if(selectedRole) loadPerms(selectedRole.id); }, [selectedRole, loadPerms]);

  const openNovo = () => { setEditRole(null); setFormNome(''); setFormDesc(''); setFormAtivo(true); setModalOpen(true); };
  const openEdit = (r: RoleDto) => { setEditRole(r); setFormNome(r.nome); setFormDesc(r.descricao ?? ''); setFormAtivo(r.ativo); setModalOpen(true); };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!formNome.trim()) return error('Nome obrigatório');
    setSaving(true);
    try {
      if (editRole) {
        const r = await rolesApi.update(editRole.id, { nome: formNome.trim(), descricao: formDesc.trim() || undefined, ativo: formAtivo, versao: editRole.versao });
        if (r.success) { success('Role atualizado'); setModalOpen(false); loadRoles(); }
        else error(r.message ?? 'Erro');
      } else {
        const r = await rolesApi.create({ nome: formNome.trim(), descricao: formDesc.trim() || undefined });
        if (r.success) { success('Role criado'); setModalOpen(false); loadRoles(); if(r.data) setSelectedRole(r.data); }
        else error(r.message ?? 'Erro');
      }
    } catch (e: unknown){ error(e instanceof Error ? e.message : 'Erro'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r: RoleDto) => {
    if(!confirm(`Eliminar role "${r.nome}"?`)) return;
    try {
      const res = await rolesApi.delete(r.id);
      if(res.success){ success('Eliminado'); if(selectedRole?.id===r.id) setSelectedRole(null); loadRoles(); }
      else error(res.message ?? 'Erro');
    } catch (e: unknown){ error(e instanceof Error ? e.message : 'Erro'); }
  };

  const togglePerm = (recurso: string, field: keyof PermissaoDto, val: boolean) => {
    setPerms(prev => prev.map(p => p.recurso===recurso ? { ...p, [field]: val } : p));
  };

  const savePerms = async () => {
    if(!selectedRole) return;
    try {
      // só atualiza quem já tem id (criado pelo backend)
      const updates = perms.filter(p => p.id !== 0);
      for (const p of updates) {
        // Pass the current version from the perms state to handle concurrency
        await permissoesApi.update(p.id, p);
      }
      success('Permissões guardadas');
      loadPerms(selectedRole.id);
    } catch (e: unknown){ error(e instanceof Error ? e.message : 'Erro ao guardar'); }
  };

  if (loading) return <div className="page-container"><div className="card" style={{padding:24}}><div className="page-loading"><div className="spinner"></div><p>A carregar roles...</p></div></div></div>;

  return (
    <div className="page-container" style={{maxWidth:1400}}>
      <div className="page-header">
        <h1 className="page-title">Roles e Permissões</h1>
        <button className="btn btn-primary" onClick={openNovo}><Plus className="w-4 h-4"/> Novo Role</button>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'320px 1fr', gap:16}}>
        <div className="card" style={{padding:12}}>
          <h3 style={{display:'flex',gap:8,alignItems:'center',marginBottom:12}}><Shield className="w-5 h-5"/>Roles</h3>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {roles.map(r=> (
              <div key={r.id} onClick={()=>setSelectedRole(r)} style={{padding:'10px 12px', borderRadius:8, cursor:'pointer', background: selectedRole?.id===r.id ? 'var(--c-surface-3)' : 'var(--c-surface-1)', border:'1px solid var(--c-border)', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600}}>{r.nome} <span style={{opacity:0.6,fontSize:11}}>#{r.id}</span></div>
                  <div style={{fontSize:12, opacity:0.7}}>{r.descricao ?? ''} {!r.ativo && '(inativo)'}</div>
                </div>
                <div style={{display:'flex',gap:4}}>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={(e)=>{e.stopPropagation(); openEdit(r);}}><Edit className="w-4 h-4"/></button>
                  <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={(e)=>{e.stopPropagation(); handleDelete(r);}} disabled={r.id<=2}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
            {roles.length===0 && <p style={{opacity:0.6}}>Nenhum role</p>}
          </div>
        </div>
        <div className="card" style={{padding:16}}>
          {!selectedRole ? <p>Selecione um role</p> : (
            <>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <div>
                  <h3>Permissões — {selectedRole.nome}</h3>
                  <p className="form-hint">As permissões são verificadas no servidor em cada operação. Desmarcar “Aceder” bloqueia todas as restantes ações desse recurso.</p>
                </div>
                <button className="btn btn-primary btn-sm" onClick={savePerms} disabled={permsLoading}><Save className="w-4 h-4"/> Guardar</button>
              </div>
              <div className="permission-explainer" aria-label="Significado das permissões">
                <span><strong>Aceder</strong> consultar o recurso</span>
                <span><strong>Criar</strong> adicionar registos</span>
                <span><strong>Editar</strong> alterar registos</span>
                <span><strong>Eliminar</strong> apagar registos</span>
                <span><strong>Gerir</strong> administrar definições</span>
              </div>
              {permsLoading ? <div className="page-loading"><div className="spinner"></div></div> : (
                <div style={{overflow:'auto'}}>
                  <table className="table">
                    <thead><tr><th>Recurso</th><th title={PERMISSION_HELP.podeAceder}>Aceder</th><th title={PERMISSION_HELP.podeCriar}>Criar</th><th title={PERMISSION_HELP.podeEditar}>Editar</th><th title={PERMISSION_HELP.podeEliminar}>Eliminar</th><th title={PERMISSION_HELP.podeGerir}>Gerir</th></tr></thead>
                    <tbody>
                      {perms.map(p=> (
                        <tr key={p.recurso}>
                          <td style={{fontWeight:500}}>{p.recurso}</td>
                          <td><input type="checkbox" checked={!!p.podeAceder} onChange={e=>togglePerm(p.recurso,'podeAceder',e.target.checked)} /></td>
                          <td><input type="checkbox" checked={!!p.podeCriar} onChange={e=>togglePerm(p.recurso,'podeCriar',e.target.checked)} /></td>
                          <td><input type="checkbox" checked={!!p.podeEditar} onChange={e=>togglePerm(p.recurso,'podeEditar',e.target.checked)} /></td>
                          <td><input type="checkbox" checked={!!p.podeEliminar} onChange={e=>togglePerm(p.recurso,'podeEliminar',e.target.checked)} /></td>
                          <td><input type="checkbox" checked={!!p.podeGerir} onChange={e=>togglePerm(p.recurso,'podeGerir',e.target.checked)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p style={{fontSize:12, opacity:0.6, marginTop:8}}>No utilizador apenas atribui o role; as permissões são verificadas pelo role do user.</p>
            </>
          )}
        </div>
      </div>
      <Modal title={editRole ? 'Editar Role' : 'Novo Role'} open={modalOpen} onClose={()=>setModalOpen(false)} footer={
        <><button className="btn btn-secondary" onClick={()=>setModalOpen(false)}>Cancelar</button><button className="btn btn-primary" onClick={handleSaveRole} disabled={saving}>{saving?'A guardar...': editRole?'Atualizar':'Criar'}</button></>
      }>
        <form onSubmit={handleSaveRole}>
          <div className="form-group"><label className="label">Nome *</label><input className="input" value={formNome} onChange={e=>setFormNome(e.target.value)} placeholder="Ex: Gestor" required /></div>
          <div className="form-group"><label className="label">Descrição</label><input className="input" value={formDesc} onChange={e=>setFormDesc(e.target.value)} placeholder="Descrição" /></div>
          {editRole && <div className="form-group form-checkbox"><label className="checkbox-label"><input type="checkbox" checked={formAtivo} onChange={e=>setFormAtivo(e.target.checked)} /><span>Ativo</span></label></div>}
        </form>
      </Modal>
    </div>
  );
}
